import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { COMPANY } from "../../lib/constants";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Engines", href: "#engines" },
  { label: "Aircrafts", href: "#capabilities" },
  { label: "Team", href: "#team" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-24" : "h-32 lg:h-36"}`}>
          {/* Logo — transparent PNG, no chip needed */}
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center group"
          >
            <img
              src={COMPANY.logo}
              alt="Noble Power Solutions"
              className={`object-contain transition-all duration-300 ${
                scrolled ? "h-20 lg:h-24" : "h-32 lg:h-40"
              }`}
              style={{
                filter: scrolled
                  ? "none"
                  : "drop-shadow(0 0 14px rgba(255,255,255,0.45)) drop-shadow(0 0 40px rgba(201,169,97,0.4)) drop-shadow(0 6px 20px rgba(0,0,0,0.55))",
              }}
            />
            <span className="sr-only">Noble Power Solutions — Home</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  scrolled
                    ? "text-[#111827] hover:text-[#0B1D3A]"
                    : "text-white/90 hover:text-[#C9A961]"
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-rfq-button"
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold tracking-wide rounded-sm transition-colors ${
                scrolled
                  ? "bg-[#0B1D3A] hover:bg-[#1E3A5F] text-white"
                  : "bg-[#C9A961] hover:bg-[#B0904A] text-[#0B1D3A]"
              }`}
            >
              Request a Quote
            </a>
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#0B1D3A]" : "text-white"}`}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E5E7EB] shadow-md">
          <div className="px-5 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block py-3 text-[#111827] font-medium border-b border-[#F1F2F4]"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-rfq-button"
              className="flex items-center justify-center gap-2 mt-3 bg-[#0B1D3A] text-white px-4 py-3 font-semibold rounded-sm"
            >
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
